import { format } from "date-fns";
import { NotificationsEmpty } from "@/components/common/empty-states";
import { ptBR } from "date-fns/locale";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
  CalendarClock,
  Activity,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface NotificationListProps {
  onClose?: () => void;
}

const typeConfig: Record<
  Notification["type"],
  { icon: React.ReactNode; color: string }
> = {
  info: {
    icon: <Info className="h-4 w-4" />,
    color: "bg-blue-500",
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4" />,
    color: "bg-yellow-500",
  },
  success: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: "bg-green-500",
  },
  error: {
    icon: <AlertCircle className="h-4 w-4" />,
    color: "bg-red-500",
  },
  task: {
    icon: <CalendarClock className="h-4 w-4" />,
    color: "bg-purple-500",
  },
  activity: {
    icon: <Activity className="h-4 w-4" />,
    color: "bg-cyan-500",
  },
};

export function NotificationList({ onClose }: NotificationListProps) {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    unreadCount,
  } = useNotifications();

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose?.();
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Carregando...
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          <span className="font-medium">Notificações</span>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">
              ({unreadCount} não lida{unreadCount > 1 ? "s" : ""})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate("/app/configuracoes")}
            title="Configurações de notificações"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Notifications */}
      {notifications.length === 0 ? (
        <NotificationsEmpty />
      ) : (
        <>
          <ScrollArea className="h-[300px]">
            <div className="flex flex-col">
              {notifications.map((notification) => {
                const config = typeConfig[notification.type] || typeConfig.info;

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "flex gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-b-0",
                      !notification.read && "bg-muted/30"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div
                      className={cn(
                        "flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white",
                        config.color
                      )}
                    >
                      {config.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            "text-sm line-clamp-1",
                            !notification.read && "font-medium"
                          )}
                        >
                          {notification.title}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              title="Marcar como lida"
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            title="Excluir"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {notification.message && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(
                          new Date(notification.created_at),
                          "dd/MM 'às' HH:mm",
                          { locale: ptBR }
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Footer actions */}
          <Separator />
          <div className="flex items-center justify-between p-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => markAllAsRead()}
              disabled={unreadCount === 0}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Marcar todas como lidas
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-destructive"
              onClick={() => clearAll()}
              disabled={notifications.length === 0}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Limpar tudo
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
